import { useContext, useEffect } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import { mediaQueryOnly, mediaQueryUp } from 'components/utils/breakpoint'
import Label from 'LuxKit/Label/Label'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import FormatCurrency from 'components/Common/FormatCurrency'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getBaseTierOfferWithJoiningFee } from 'selectors/luxPlusSelectors'
import { fetchProductSubscription } from 'actions/LuxPlusActions'
import useLuxPlusLandingActions from 'luxPlus/hooks/useLuxPlusLandingActions'
import useSwitchRegion from 'hooks/useSwitchRegion'
import { LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import getRegionSaveAmountCopy from 'luxPlus/lib/getRegionSaveAmountCopy'
import { getIsFreePreviewMemberOutsideRegion } from 'luxPlus/selectors/featureToggle'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import GeoContext from 'contexts/geoContext'

const BannerContainer = styled(Sheet)`
  border: ${rem(1)} solid ${props => props.theme.palette.messaging.urgency.normalForeground};
`

const PriceText = styled(Heading)`
  text-decoration: line-through;
`

const RegularWeightText = styled.span`
  font-weight: ${props => props.theme.font.primary.weight.regular};
`

const FootnoteMark = styled.sup`
  font-size: 0.55em;
`

const NoWrapLabel = styled(Label)`
  white-space: nowrap;
  flex-shrink: 0;
`

// Both buttons split the row evenly on mobile (matching the Figma mobile frame); from tablet up
// they size to their own content instead, sitting inline together.
const EqualWidthButton = styled.div`
  flex: 1 1 0;
  min-width: 0;

  ${mediaQueryUp.tablet} {
    flex: 0 0 auto;
  }
`

const BannerTextContent = styled.div`
  text-align: center;

  ${mediaQueryUp.largeDesktop} {
    text-align: start;
  }
`

const InlineLogoWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  margin: 0 ${rem(4)};
  vertical-align: -0.04em;
  color: currentColor;
`

const InlineLuxPlusLogo = styled(LuxPlusLogo)`
  display: block;
`

// Group's desktopDirection switches to a horizontal row at 992px, which is too narrow for this
// banner's content and squeezes everything onto one cramped line — keep it stacked through the
// desktop breakpoint and only go horizontal from largeDesktop (1200px) up.
const OuterGroup = styled(Group)`
  ${mediaQueryOnly.desktop} {
    flex-direction: column !important;
  }
`

// Group's tabletDirection puts "Join today for" + price on the same row as the buttons at the
// built-in tablet breakpoint (576px) — push that switch out to 700px instead, so both stay
// stacked as separate rows until then.
const JoinTodayGroup = styled(Group)`
  @media (min-width: 576px) and (max-width: 699.98px) {
    flex-direction: column !important;
  }
`

function LuxPlusMonthTakeoverJoiningFeeBanner() {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const dispatch = useAppDispatch()
  const offer = useAppSelector(getBaseTierOfferWithJoiningFee)
  const isMobile = useScreenSizeOnly('mobile')
  const { handleBecomeMember } = useLuxPlusLandingActions()
  const regionSaveAmountCopy = getRegionSaveAmountCopy(currentRegionCode, intl)
  const isFreePreviewMemberOutsideRegion = useAppSelector(getIsFreePreviewMemberOutsideRegion)
  const memberRegion = useAppSelector(state => state.auth.account.luxPlus.member.subscription.item?.region)
  const [switchRegionPath, handleSwitchRegion] = useSwitchRegion(memberRegion)

  useEffect(() => {
    dispatch(fetchProductSubscription())
  }, [dispatch])

  const ctaLabel = intl.formatMessage({
    id: 'sPE4cFBqsGZ5j-zjpyl2',
    defaultMessage: 'Join LuxPlus+',
    description: 'Call-to-action button label on the LuxPlus+ Month joining fee banner linking to the LuxPlus+ membership page',
  })

  return (
    <BannerContainer background="light-background" borderRadius="L" padding="24 20">
      <BannerTextContent>
        <OuterGroup
          direction="vertical"
          desktopDirection="horizontal"
          gap={16}
          tabletGap={24}
          desktopGap={24}
          horizontalAlign="center"
          desktopHorizontalAlign="start"
          verticalAlign="center"
          wrap="wrap"
        >
          <Group direction="vertical" desktopDirection="horizontal" gap={8} desktopGap={12} horizontalAlign="center" desktopHorizontalAlign="start" verticalAlign="center" grow>
            <NoWrapLabel kind="primary" variant="urgency">
              <FormattedMessage
                id="6wM4B9NKG0R-PcYXb_HQ"
                defaultMessage="Limited-time offer"
                description="Label badge on the LuxPlus+ Month joining fee banner indicating the offer is only available for a limited time"
              />
            </NoWrapLabel>
            <Group direction="vertical" gap={2} horizontalAlign="center" desktopHorizontalAlign="start">
              {offer && <Heading variant="heading3">
                <RegularWeightText>
                  <FormattedMessage
                    id="jJeVjd598HGZnH2-1ScT"
                    defaultMessage="{joinFee} joining fee waived<footnote>^</footnote>"
                    description="Heading on the LuxPlus+ Month joining fee banner announcing the waived membership joining fee, with a footnote marker referencing the fine print below"
                    values={{
                      joinFee: <FormatCurrency value={offer.joinFee} format="roundedDollar" />,
                      footnote: (chunks) => <FootnoteMark>{chunks}</FootnoteMark>,
                    }}
                  />
                </RegularWeightText>
              </Heading>}
              <BodyText variant="medium">
                <FormattedMessage
                  id="aDSWtUBhCdJFCrKEaq6W"
                  defaultMessage="Save {regionSaveAmountCopy} every year with LuxPlus+ member-only pricing."
                  description="Subtitle on the LuxPlus+ Month joining fee banner explaining the value of LuxPlus+ member pricing"
                  values={{ regionSaveAmountCopy }}
                />
              </BodyText>
            </Group>
          </Group>
          <JoinTodayGroup direction="vertical" tabletDirection="horizontal" gap={16} tabletGap={24} desktopGap={24} horizontalAlign="center" desktopHorizontalAlign="start" verticalAlign="center">
            {offer && <Group
              direction="horizontal"
              desktopDirection="vertical"
              gap={4}
              desktopGap={0}
              verticalAlign="baseline"
              horizontalAlign="center"
              desktopHorizontalAlign="start"
            >
              <BodyText variant="medium" weight="bold" colour="lux-plus" wrap="no-wrap">
                <FormattedMessage
                  id="L0tQlcnFcU2QaUx9zc6f"
                  defaultMessage="Join today for"
                  description="Lead-in text before the discounted membership price on the LuxPlus+ Month joining fee banner"
                />
              </BodyText>
              <Group direction="horizontal" gap={4} verticalAlign="baseline">
                <Heading variant="heading4" colour="lux-plus">
                  <FormatCurrency value={offer.price} format="roundedDollar" />
                </Heading>
                <PriceText variant="heading4" colour="lux-plus">
                  <FormatCurrency value={offer.price + offer.joinFee} format="roundedDollar" />
                </PriceText>
              </Group>
            </Group>}
            <Group direction="horizontal" gap={8} fullWidth={isMobile}>
              <ProductPaletteProvider palette="lux-plus">
                {isFreePreviewMemberOutsideRegion && <EqualWidthButton>
                  <TextButton kind="primary" size="medium" shape="round" fit="mobile-full-width" href={switchRegionPath} onClick={handleSwitchRegion}>
                    <FormattedMessage
                      id="VTICxidguqZ5x30E0c6_"
                      defaultMessage="Switch to your {luxPlusLogo} region"
                      description="CTA button to switch to the LuxPlus membership region on the landing page"
                      values={{
                        luxPlusLogo: <InlineLogoWrapper><InlineLuxPlusLogo colour="inherit" height={11} /></InlineLogoWrapper>,
                      }}
                    />
                  </TextButton>
                </EqualWidthButton>}
                {!isFreePreviewMemberOutsideRegion && <>
                  <EqualWidthButton>
                    <TextButton kind="primary" size="medium" shape="round" fit="mobile-full-width" onClick={handleBecomeMember}>
                      {ctaLabel}
                    </TextButton>
                  </EqualWidthButton>
                  <EqualWidthButton>
                    <TextButton kind="secondary" size="medium" shape="round" fit="mobile-full-width" to={`/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}`} target="_blank">
                      {intl.formatMessage({
                        id: 'eEKvpY1Aq_moq_x-dAGL',
                        defaultMessage: 'Learn more',
                        description: 'Secondary button label on the LuxPlus+ Month joining fee banner linking to the LuxPlus+ vertical page',
                      })}
                    </TextButton>
                  </EqualWidthButton>
                </>}
              </ProductPaletteProvider>
            </Group>
          </JoinTodayGroup>
        </OuterGroup>
      </BannerTextContent>
    </BannerContainer>
  )
}

export default LuxPlusMonthTakeoverJoiningFeeBanner
